"use client";

//
// The Cove Club account portal: points balance, the transaction ledger, and
// order history for the signed-in guest.
//
// Three reactive Convex reads power this page, all keyed off the customer
// JWT that CustomerAuthProvider feeds into the Convex client's single auth
// slot (see customer-auth-provider.tsx) rather than anything the client
// supplies:
//   - `api.customerAuth.currentCustomer`: who is signed in.
//   - `api.orders.getMyRewards`: points balance + recent ledger.
//   - `api.orders.listMyOrders` (via `usePaginatedQuery`): order history.
// None of these take an email or memberId argument. There is nothing here
// to spoof into someone else's account.
//
// Public brand contract only (DESIGN.md §11): tenant tokens (`--roma-*`,
// aliased to `--cove-*`), Hanken Grotesk / Fraunces, `roma-*` utility
// classes. This deliberately avoids the shadcn primitives and the shared
// admin/ops semantic color slots those primitives resolve against, and it
// never reaches into the ops theme scope. This is the same contract
// src/app/(public)/cove-club/terms/page.tsx uses.

import { useEffect, useState } from "react";
import Link from "next/link";
import {
  useConvexAuth,
  useMutation,
  usePaginatedQuery,
  useQuery,
} from "convex/react";
import { Loader2 } from "lucide-react";
import { api } from "../../../../convex/_generated/api";
import { useCustomerAuth } from "@/components/customer-auth/customer-auth-provider";

type LoyaltyTier = "bronze" | "silver" | "gold";

const TIER_LABELS: Record<LoyaltyTier, string> = {
  bronze: "Bronze",
  silver: "Silver",
  gold: "Gold",
};

/**
 * Lifetime-point thresholds a guest needs to reach each tier, mirrored here
 * only for display math (how far a guest already past a threshold sits
 * inside it). The source of truth for the thresholds themselves, and for
 * which tier a member is actually in, is `convex/lib/loyaltyTiers.ts` via
 * the `tier`/`nextTier`/`pointsToNextTier` fields `getMyRewards` returns.
 * This panel never derives a tier client-side.
 */
const TIER_FLOOR: Record<LoyaltyTier, number> = {
  bronze: 0,
  silver: 300,
  gold: 1000,
};

const ORDER_STATUS_LABELS: Record<string, string> = {
  pending: "Pending",
  confirmed: "Confirmed",
  preparing: "Preparing",
  ready: "Ready",
  out_for_delivery: "Out for delivery",
  completed: "Completed",
  cancelled: "Cancelled",
};

/**
 * `rewards_transactions.source` is machine-readable provenance (see the
 * schema comment in convex/schema.ts): "order_base", "referral",
 * "birthday_2027", "streak_week3", "challenge:<id>", "feedback:<orderId>",
 * "surprise". This turns that into something a guest can read. Falls back to
 * the raw value for a source this hasn't been taught yet, rather than hiding
 * it, so a new campaign source is visible (if unpolished) instead of blank.
 */
function humanizeSource(source: string | undefined): string | null {
  if (!source) return null;
  if (source === "order_base") return "Order";
  if (source === "referral") return "Referral bonus";
  if (source === "surprise") return "Surprise bonus";
  if (source.startsWith("birthday")) return "Birthday bonus";
  if (source.startsWith("streak")) return "Visit streak bonus";
  if (source.startsWith("challenge")) return "Challenge bonus";
  if (source.startsWith("feedback")) return "Feedback bonus";
  return source;
}

/**
 * Progress toward the next $5 reward. 100 points = $5, so this is a simple
 * modulo: how far into the current 100-point cycle the guest's balance sits.
 *
 * A balance at or below zero shows zero progress on the bar rather than a
 * negative or wrapped-around fill, since there is no partial reward to show
 * until the guest is back above zero. The distance to the next reward is
 * still computed for real, though: a guest at -40 needs 140 points, not 100,
 * because a negative balance means they have to earn their way past zero
 * before they start earning toward the next $5. Reporting a flat 100 there
 * would understate how far away the reward actually is.
 */
function nextRewardProgress(pointsBalance: number): {
  percent: number;
  pointsRemaining: number;
} {
  if (pointsBalance <= 0) {
    return { percent: 0, pointsRemaining: 100 - pointsBalance };
  }
  const pointsIntoCycle = pointsBalance % 100;
  if (pointsIntoCycle === 0) {
    return { percent: 100, pointsRemaining: 0 };
  }
  return { percent: pointsIntoCycle, pointsRemaining: 100 - pointsIntoCycle };
}

/**
 * Progress across the current tier's span, purely for the bar's fill percent.
 * `tier`/`nextTier`/`pointsToNextTier` are the server-derived source of
 * truth; this only turns them into a 0-100 number to draw.
 *
 * Gold has no `nextTier`, so it always reads as a full bar rather than
 * dividing by a threshold that does not exist. Everything else is clamped to
 * [0, 100]: `lifetimePoints` can only ever be at or above the current tier's
 * floor (that is what put the guest in this tier), so this is a defensive
 * floor, not a case this panel expects to hit.
 */
function tierProgressPercent(
  tier: LoyaltyTier,
  lifetimePoints: number,
): number {
  if (tier === "gold") return 100;
  const floor = TIER_FLOOR[tier];
  const ceiling = tier === "bronze" ? TIER_FLOOR.silver : TIER_FLOOR.gold;
  const span = ceiling - floor;
  if (span <= 0) return 100;
  const percent = ((lifetimePoints - floor) / span) * 100;
  return Math.min(100, Math.max(0, percent));
}

function pluralizePoints(count: number): string {
  return count === 1 ? "point" : "points";
}

function formatPointsDollarValue(pointsBalance: number): string {
  const dollars = (pointsBalance / 100) * 5;
  const sign = dollars < 0 ? "-" : "";
  return `${sign}$${Math.abs(dollars).toFixed(2)}`;
}

function formatSignedPoints(points: number): string {
  return points > 0 ? `+${points}` : `${points}`;
}

function formatOrderTotal(totalCents: number): string {
  return `$${(totalCents / 100).toFixed(2)}`;
}

function formatDate(timestampMs: number): string {
  return new Date(timestampMs).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function StatusMessage({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-sm text-[var(--roma-espresso)]/70">{children}</p>
  );
}

function SignInPrompt({ message }: { message: string }) {
  return (
    <div className="space-y-4">
      <StatusMessage>{message}</StatusMessage>
      <Link
        href="/club/login"
        className="roma-cta inline-flex rounded-full px-5 py-2.5 text-sm font-medium"
      >
        Sign in
      </Link>
    </div>
  );
}

export function AccountPanel() {
  const { isLoading, isAuthenticated } = useConvexAuth();
  const { signOut } = useCustomerAuth();
  const member = useQuery(
    api.customerAuth.currentCustomer,
    isAuthenticated ? {} : "skip",
  );
  const rewards = useQuery(
    api.orders.getMyRewards,
    isAuthenticated ? {} : "skip",
  );
  const orderHistory = usePaginatedQuery(
    api.orders.listMyOrders,
    isAuthenticated ? {} : "skip",
    { initialNumItems: 10 },
  );
  const mintReferralCode = useMutation(api.orders.mintReferralCode);
  const [mintAttempted, setMintAttempted] = useState(false);
  const [copied, setCopied] = useState(false);

  // `getMyRewards` reports `referralCode: null` for a member who has never
  // had one minted. `mintReferralCode` (convex/orders.ts) is idempotent, so
  // this quietly gets one on file the first time the portal loads for that
  // guest, rather than making them click a button just to see their code.
  // Guarded by `mintAttempted` so this fires once per mount, not once per
  // render while `rewards` is still loading.
  useEffect(() => {
    if (!isAuthenticated) return;
    if (rewards === undefined || rewards === null) return;
    if (rewards.referralCode) return;
    if (mintAttempted) return;
    setMintAttempted(true);
    void mintReferralCode({});
  }, [isAuthenticated, rewards, mintAttempted, mintReferralCode]);

  if (isLoading) {
    return <StatusMessage>Loading your account...</StatusMessage>;
  }

  if (!isAuthenticated) {
    return <SignInPrompt message="Sign in to see your points and past orders." />;
  }

  if (member === undefined) {
    return <StatusMessage>Loading your account...</StatusMessage>;
  }

  if (member === null) {
    return (
      <SignInPrompt message="We could not find your account. Try signing in again." />
    );
  }

  const progress = nextRewardProgress(rewards?.pointsBalance ?? 0);

  return (
    <div className="space-y-10">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm text-[var(--roma-espresso)]/70">Signed in as</p>
          <p className="text-lg font-medium text-[var(--roma-espresso)]">
            {member.email}
          </p>
        </div>
        <button
          type="button"
          onClick={() => void signOut()}
          className="roma-link shrink-0 text-sm text-[var(--roma-espresso)]/70"
        >
          Sign out
        </button>
      </div>

      {/* Balance */}
      <section className="roma-card rounded-xl p-6">
        <p className="text-sm text-[var(--roma-espresso)]/70">Points balance</p>
        {rewards === undefined ? (
          <p className="mt-2 text-sm text-[var(--roma-espresso)]/70">
            Loading your balance...
          </p>
        ) : rewards === null ? (
          <p className="mt-2 text-sm text-[var(--roma-espresso)]/70">
            We could not load your balance right now.
          </p>
        ) : (
          <>
            <p className="mt-1 text-4xl font-semibold tabular-nums text-[var(--roma-espresso)]">
              {rewards.pointsBalance}
            </p>
            <p className="mt-1 text-sm text-[var(--roma-espresso)]/70">
              Worth {formatPointsDollarValue(rewards.pointsBalance)}
              {rewards.pointsBalance < 0
                ? " toward your next order (your balance went negative because points from a refunded order were reversed)"
                : ""}
            </p>
            <div className="mt-4">
              <div className="h-2 w-full overflow-hidden rounded-full bg-[var(--roma-parchment-line)]">
                <div
                  className="h-full rounded-full bg-[var(--roma-terracotta)] transition-all"
                  style={{ width: `${progress.percent}%` }}
                />
              </div>
              <p className="mt-2 text-xs text-[var(--roma-espresso)]/60">
                {progress.pointsRemaining === 0
                  ? "You have enough for a $5 reward right now."
                  : `${progress.pointsRemaining} more ${pluralizePoints(progress.pointsRemaining)} to your next $5 (100 points = $5).`}
              </p>
            </div>
            <p className="mt-4 text-sm text-[var(--roma-espresso)]/60">
              {rewards.lifetimePoints} points earned all time
            </p>
          </>
        )}
      </section>

      {/* Tier */}
      {rewards ? (
        <section className="roma-card rounded-xl p-6">
          <div className="flex items-center justify-between gap-4">
            <p className="text-sm text-[var(--roma-espresso)]/70">
              Membership tier
            </p>
            <span className="roma-eyebrow inline-flex items-center rounded-full border border-[var(--roma-parchment-line)] px-3 py-1 text-xs text-[var(--roma-terracotta)]">
              {TIER_LABELS[rewards.tier]}
            </span>
          </div>
          <div className="mt-4">
            <div className="h-2 w-full overflow-hidden rounded-full bg-[var(--roma-parchment-line)]">
              <div
                className="h-full rounded-full bg-[var(--roma-terracotta)] transition-all"
                style={{
                  width: `${tierProgressPercent(rewards.tier, rewards.lifetimePoints)}%`,
                }}
              />
            </div>
            <p className="mt-2 text-xs text-[var(--roma-espresso)]/60">
              {rewards.nextTier && rewards.pointsToNextTier !== null
                ? `${rewards.pointsToNextTier} more lifetime ${pluralizePoints(rewards.pointsToNextTier)} to reach ${TIER_LABELS[rewards.nextTier]}.`
                : "You are at Gold, our top tier."}
            </p>
          </div>
          <p className="mt-4 text-xs leading-relaxed text-[var(--roma-espresso)]/60">
            Your tier is based on lifetime points, so it can move in either
            direction. If a past order is refunded, the points it earned come
            out of your lifetime total, and that can drop your tier. That is
            expected, not a mistake on your account, and your tier will rise
            again as you keep ordering.
          </p>
        </section>
      ) : null}

      {/* Referral */}
      {rewards ? (
        <section className="roma-card rounded-xl p-6">
          <p className="text-sm text-[var(--roma-espresso)]/70">
            Refer a friend
          </p>
          {rewards.referralCode ? (
            <>
              <p className="mt-1 text-2xl font-semibold tracking-wide tabular-nums text-[var(--roma-espresso)]">
                {rewards.referralCode}
              </p>
              <p className="mt-2 text-sm text-[var(--roma-espresso)]/70">
                Share this line so a friend can use it: Enter my code{" "}
                {rewards.referralCode} at checkout on your first order and we
                both earn points.
              </p>
              <button
                type="button"
                onClick={() => {
                  const shareText = `Enter my code ${rewards.referralCode} at checkout on your first order and we both earn points.`;
                  void navigator.clipboard
                    .writeText(shareText)
                    .then(() => {
                      setCopied(true);
                      setTimeout(() => setCopied(false), 2000);
                    });
                }}
                className="roma-cta mt-4 inline-flex rounded-full px-5 py-2.5 text-sm font-medium"
              >
                {copied ? "Copied" : "Copy invite to share"}
              </button>
            </>
          ) : (
            <p className="mt-2 text-sm text-[var(--roma-espresso)]/70">
              Setting up your invite code...
            </p>
          )}
        </section>
      ) : null}

      {/* Ledger */}
      <section>
        <h2 className="font-serif text-xl font-normal text-[var(--roma-espresso)]">
          Points history
        </h2>
        {rewards === undefined ? (
          <StatusMessage>Loading your points history...</StatusMessage>
        ) : rewards === null || rewards.recentTransactions.length === 0 ? (
          <StatusMessage>
            No points activity yet. It shows up here as soon as you order.
          </StatusMessage>
        ) : (
          <>
            <p className="mt-1 text-xs text-[var(--roma-espresso)]/60">
              Your most recent activity, newest first.
            </p>
            <ul className="mt-3 divide-y divide-[var(--roma-parchment-line)]">
            {rewards.recentTransactions.map((txn) => {
              const sourceLabel = humanizeSource(txn.source);
              return (
                <li
                  key={txn._id}
                  className="flex items-start justify-between gap-4 py-3"
                >
                  <div>
                    <p className="text-sm text-[var(--roma-espresso)]">
                      {txn.description}
                    </p>
                    <p className="mt-0.5 text-xs text-[var(--roma-espresso)]/60">
                      {formatDate(txn.createdAt)}
                      {sourceLabel ? ` · ${sourceLabel}` : ""}
                    </p>
                  </div>
                  <p
                    className={`shrink-0 text-sm font-medium tabular-nums ${
                      txn.points < 0
                        ? "text-[var(--roma-wine)]"
                        : "text-[var(--roma-olive)]"
                    }`}
                  >
                    {formatSignedPoints(txn.points)}
                  </p>
                </li>
              );
            })}
            </ul>
          </>
        )}
      </section>

      {/* Order history */}
      <section>
        <h2 className="font-serif text-xl font-normal text-[var(--roma-espresso)]">
          Order history
        </h2>
        {orderHistory.status === "LoadingFirstPage" ? (
          <StatusMessage>Loading your orders...</StatusMessage>
        ) : orderHistory.results.length === 0 ? (
          <StatusMessage>
            No orders yet. Your first order will show up here.
          </StatusMessage>
        ) : (
          <>
            <ul className="mt-4 space-y-3">
              {orderHistory.results.map((order) => (
                <li key={order._id} className="roma-card-muted rounded-lg p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-sm font-medium text-[var(--roma-espresso)]">
                        Order {order.orderNumber}
                      </p>
                      <p className="mt-0.5 text-xs text-[var(--roma-espresso)]/60">
                        {formatDate(order.createdAt)} ·{" "}
                        {ORDER_STATUS_LABELS[order.status] ?? order.status}
                      </p>
                    </div>
                    <p className="shrink-0 text-sm font-medium tabular-nums text-[var(--roma-espresso)]">
                      {formatOrderTotal(order.total)}
                    </p>
                  </div>
                  {order.itemSummary ? (
                    <p className="mt-2 text-sm text-[var(--roma-espresso)]/70">
                      {order.itemSummary}
                    </p>
                  ) : null}
                  {order.rewardsPointsEarned || order.rewardsPointsUsed ? (
                    <p className="mt-2 text-xs text-[var(--roma-espresso)]/60">
                      {order.rewardsPointsEarned
                        ? `Earned ${order.rewardsPointsEarned} points`
                        : ""}
                      {order.rewardsPointsEarned && order.rewardsPointsUsed
                        ? " · "
                        : ""}
                      {order.rewardsPointsUsed
                        ? `Used ${order.rewardsPointsUsed} points`
                        : ""}
                    </p>
                  ) : null}
                </li>
              ))}
            </ul>
            {orderHistory.status === "CanLoadMore" ||
            orderHistory.status === "LoadingMore" ? (
              <button
                type="button"
                onClick={() => orderHistory.loadMore(10)}
                disabled={orderHistory.status === "LoadingMore"}
                className="roma-link mt-4 inline-flex items-center gap-2 text-sm text-[var(--roma-espresso)]/70 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {orderHistory.status === "LoadingMore" ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : null}
                Load more
              </button>
            ) : null}
          </>
        )}
      </section>
    </div>
  );
}
